import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput, TextareaInput, FieldRow } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { normalizeEmployeeLogin } from "@/lib/auth";
import {
  createMonteurProposal,
  updateMonteurProposal,
  deleteMonteurProposal,
  DEFAULT_PLANUNG_COLOR,
  PLANUNG_COLORS,
} from "@/lib/montageplanung";
import type {
  PlanungAbsence,
  PlanungAssignment,
  PlanungOrderLink,
  PlanungRow,
} from "@/lib/montageplanung";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { formatDateDe } from "./dateUtils";
import { AssignmentOrdersField } from "./AssignmentOrdersField";
import { AssignmentFlagCard } from "./AssignmentDialog";
import {
  EmployeeMentionField,
  type MentionedEmployee,
} from "./EmployeeMentionField";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";
import type { ConfirmDialogOptions } from "./useConfirmDialog";

function inviteesFromLinkedAssignments(
  assignment: PlanungAssignment | null,
  rows: PlanungRow[],
  assignments: PlanungAssignment[],
): MentionedEmployee[] {
  if (!assignment) return [];
  const peerIds = new Set(assignment.linked_assignment_ids ?? []);
  if (!peerIds.size) return [];
  const rowsById = new Map(rows.map((row) => [row.id, row]));
  const out: MentionedEmployee[] = [];
  const seen = new Set<string>();
  for (const peer of assignments) {
    if (!peerIds.has(peer.id)) continue;
    const row = rowsById.get(peer.row_id);
    const login = normalizeEmployeeLogin(row?.employee_login || "");
    if (!login || seen.has(login)) continue;
    seen.add(login);
    out.push({
      login,
      display: (row?.label || login).trim() || login,
    });
  }
  return out;
}

function linkedResourceIds(
  assignment: PlanungAssignment | null,
  resourceRows: PlanungRow[],
  assignments: PlanungAssignment[],
  anchorRowId: number | null,
): number[] {
  if (!assignment) return [];
  const peerIds = new Set(assignment.linked_assignment_ids ?? []);
  if (!peerIds.size) return [];
  const byId = new Map(resourceRows.map((row) => [row.id, row]));
  const out: number[] = [];
  const seen = new Set<number>();
  for (const peer of assignments) {
    if (!peerIds.has(peer.id)) continue;
    const planRow = byId.get(peer.row_id);
    if (!planRow || planRow.kind !== "resource") continue;
    if (anchorRowId != null && planRow.id === anchorRowId) continue;
    if (seen.has(planRow.id)) continue;
    seen.add(planRow.id);
    out.push(planRow.id);
  }
  return out;
}

function viewerIsParticipant(
  row: PlanungRow | null,
  assignment: PlanungAssignment | null,
  employeeRows: PlanungRow[],
  boardAssignments: PlanungAssignment[],
  viewerLogin: string,
): boolean {
  const viewer = normalizeEmployeeLogin(viewerLogin);
  if (!viewer) return false;
  if (normalizeEmployeeLogin(row?.employee_login || "") === viewer) return true;
  if (!assignment) return false;
  return inviteesFromLinkedAssignments(assignment, employeeRows, boardAssignments).some(
    (person) => person.login === viewer,
  );
}

export interface MonteurProposalDialogProps {
  open: boolean;
  onClose: () => void;
  row: PlanungRow | null;
  assignment: PlanungAssignment | null;
  defaultDate: string;
  rowAbsences: PlanungAbsence[];
  employeeRows?: PlanungRow[];
  resourceRows?: PlanungRow[];
  boardAssignments?: PlanungAssignment[];
  viewerLogin?: string | null;
  viewerRowId?: number | null;
  onSaved: () => void;
  onConfirm?: (options: ConfirmDialogOptions) => Promise<boolean>;
}

export function MonteurProposalDialog({
  open,
  onClose,
  row,
  assignment,
  defaultDate,
  rowAbsences,
  employeeRows = [],
  resourceRows = [],
  boardAssignments = [],
  viewerLogin = null,
  viewerRowId = null,
  onSaved,
  onConfirm,
}: MonteurProposalDialogProps) {
  const [title, setTitle] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_PLANUNG_COLOR);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isFixed, setIsFixed] = useState(false);
  const [isBlocker, setIsBlocker] = useState(false);
  const [note, setNote] = useState("");
  const [orders, setOrders] = useState<PlanungOrderLink[]>([]);
  const [invitees, setInvitees] = useState<MentionedEmployee[]>([]);
  const [resourceIds, setResourceIds] = useState<number[]>([]);
  const [includeSelf, setIncludeSelf] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assignmentId = assignment?.id ?? null;
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (assignment) {
      setTitle(assignment.title);
      setColor(assignment.color || DEFAULT_PLANUNG_COLOR);
      setDateFrom(assignment.date_from.slice(0, 10));
      setDateTo(assignment.date_to.slice(0, 10));
      setIsFixed(assignment.is_fixed);
      setIsBlocker(assignment.is_blocker ?? false);
      setNote(assignment.note || "");
      setOrders(assignment.orders ?? []);
      const peers = inviteesFromLinkedAssignments(assignment, employeeRows, boardAssignments);
      const viewer = normalizeEmployeeLogin(viewerLogin || "");
      setIncludeSelf(
        viewerIsParticipant(row, assignment, employeeRows, boardAssignments, viewer),
      );
      setInvitees(viewer ? peers.filter((person) => person.login !== viewer) : peers);
      setResourceIds(
        linkedResourceIds(assignment, resourceRows, boardAssignments, row?.id ?? null),
      );
    } else {
      setTitle("");
      setColor(row?.kind === "resource" && row.color ? row.color : DEFAULT_PLANUNG_COLOR);
      setDateFrom(defaultDate);
      setDateTo(defaultDate);
      setIsFixed(false);
      setIsBlocker(false);
      setNote("");
      setOrders([]);
      setInvitees([]);
      setResourceIds([]);
      const onOwnRow = viewerRowId != null && row?.id === viewerRowId;
      setIncludeSelf(onOwnRow || row?.kind === "resource");
    }
    // employeeRows/boardAssignments nur als Startwerte beim Öffnen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assignmentId, defaultDate, row?.id, viewerLogin, viewerRowId]);

  const anchorIsSelf = viewerRowId != null && row?.id === viewerRowId;
  const anchorIsResource = row?.kind === "resource";
  const anchorName = (
    row?.label || row?.employee_login || (anchorIsResource ? "die Ressource" : "den Kollegen")
  ).trim();
  const planningOnlyOthers = !includeSelf;
  const anchorIsPlanned = includeSelf || !anchorIsSelf;
  const otherResources = resourceRows.filter(
    (item) => item.active !== false && item.id !== row?.id,
  );

  const conflicts = useMemo(() => {
    if (!anchorIsPlanned || !dateFrom || !dateTo) return [];
    return rowAbsences.filter(
      (a) => a.date_from.slice(0, 10) <= dateTo && a.date_to.slice(0, 10) >= dateFrom,
    );
  }, [anchorIsPlanned, rowAbsences, dateFrom, dateTo]);

  async function handleSave() {
    if (!row) return;
    if (!title.trim()) {
      setError("Bitte einen Titel eingeben.");
      return;
    }
    if (dateTo < dateFrom) {
      setError("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }
    if (!includeSelf && anchorIsSelf && invitees.length === 0 && resourceIds.length === 0) {
      setError(
        "Bitte mindestens einen Kollegen oder eine Ressource auswählen. Sie selbst sind bei diesem Termin nicht dabei.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      row_id: row.id,
      title: title.trim(),
      color,
      date_from: dateFrom,
      date_to: dateTo,
      is_fixed: isFixed,
      is_blocker: isBlocker,
      note: note.trim() || null,
      include_self: includeSelf,
      invite_employee_logins: invitees.map((person) => person.login),
      resource_row_ids: resourceIds,
      orders,
    };
    try {
      if (assignment) {
        await updateMonteurProposal(assignment.id, payload);
      } else {
        await createMonteurProposal(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!assignment) return;
    const ok = onConfirm
      ? await onConfirm({
          title: "Terminantrag zurückziehen",
          message: `Den Terminantrag „${assignment.title}" wirklich zurückziehen?`,
          confirmLabel: "Zurückziehen",
          variant: "danger",
        })
      : window.confirm(`Terminantrag „${assignment.title}" wirklich zurückziehen?`);
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await deleteMonteurProposal(assignment.id);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        assignment
          ? "Terminantrag bearbeiten"
          : anchorIsResource
            ? `${anchorName} beantragen`
            : planningOnlyOthers && !anchorIsSelf
              ? `Termin für ${anchorName} beantragen`
              : "Termin beantragen"
      }
      size="md"
      fullScreenMobile
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <div className="flex w-full flex-wrap justify-between gap-2">
          {assignment ? (
            <Button
              type="button"
              variant="danger"
              loading={saving}
              onClick={() => void handleDelete()}
            >
              Zurückziehen
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
            <Button type="button" variant="primary" loading={saving} onClick={() => void handleSave()}>
              {assignment ? "Speichern" : "Antrag stellen"}
            </Button>
          </div>
        </div>
      }
    >
      <p className="mb-4 text-xs leading-relaxed text-label-secondary">
        {anchorIsResource && !includeSelf
          ? `Sie beantragen die Ressource „${anchorName}“, ohne selbst eingeplant zu werden. Der Eintrag bleibt gestrichelt, bis das Büro ihn freigibt oder ablehnt. Weitere Kollegen können Sie per @ dazunehmen.`
          : anchorIsResource
            ? `Die Ressource „${anchorName}“ wird zusammen mit Ihrem Einsatz beantragt. Der Eintrag bleibt gestrichelt, bis das Büro ihn freigibt oder ablehnt. Weitere Kollegen und Ressourcen können Sie dazunehmen.`
            : planningOnlyOthers && !anchorIsSelf
              ? `Der Termin wird für ${anchorName} beantragt, ohne dass Sie selbst eingeplant werden. Er bleibt gestrichelt, bis das Büro ihn freigibt oder ablehnt. Weitere Kollegen können Sie per @ dazunehmen, Ressourcen über die Auswahl unten.`
              : planningOnlyOthers
                ? "Sie beantragen den Termin nur für andere Kollegen oder Ressourcen und sind selbst nicht eingeplant. Wählen Sie mindestens einen Kollegen oder eine Ressource. Das Büro gibt den Antrag frei."
                : "Der Termin ist zunächst nur für Sie, eingeladene Kollegen und beantragte Ressourcen sichtbar (gestrichelt) und wird nach Prüfung durch das Büro freigegeben oder abgelehnt."}
      </p>

      {error ? (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <div className="space-y-3">
        <FieldRow label="Titel">
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </FieldRow>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldRow label="Von">
            <TextInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FieldRow>
          <FieldRow label="Bis">
            <TextInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FieldRow>
        </div>

        <FieldRow label="Farbe">
          <div className="flex flex-wrap gap-2">
            {PLANUNG_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                className={`h-7 w-7 rounded-full border-2 ${
                  color === c.value ? "border-label" : "border-transparent"
                }`}
                style={{ backgroundColor: c.value }}
                aria-label={`Farbe ${c.label}`}
                onClick={() => setColor(c.value)}
              />
            ))}
          </div>
        </FieldRow>

        <div className="grid grid-cols-1 gap-2">
          <AssignmentFlagCard
            id="mp-proposal-fixed"
            tone="fixed"
            title="Fixer Termin (!)"
            description="Markiert den Einsatz als verbindlich/fix."
            checked={isFixed}
            onChange={setIsFixed}
          />
          <AssignmentFlagCard
            id="mp-proposal-blocker"
            tone="blocker"
            title="Blocker"
            description="Gestrichelter Rahmen – hebt den Einsatz als Blocker hervor."
            checked={isBlocker}
            onChange={setIsBlocker}
          />
        </div>

        <AssignmentOrdersField
          orders={orders}
          onChange={setOrders}
          onFirstAdded={(order) => {
            if (!title.trim()) setTitle(formatAuftragLookupLabel(order));
          }}
        />

        <Checkbox
          id="mp-proposal-include-self"
          checked={includeSelf}
          onChange={(e) => setIncludeSelf(e.target.checked)}
          label="Ich bin bei diesem Termin dabei"
          description="Abwählen, um den Termin nur für Kollegen oder Ressourcen zu planen – ohne die eigene Zeile."
        />

        <FieldRow
          label={planningOnlyOthers && anchorIsSelf ? "Kollegen" : "Weitere Mitarbeiter"}
          hint={
            planningOnlyOthers && anchorIsSelf
              ? "Mindestens ein Kollege oder eine Ressource. Der Termin liegt nur auf deren Zeilen, nicht auf Ihrer."
              : "Per @ Kollegen einladen – sie erhalten denselben Terminantrag auf ihrer Zeile."
          }
        >
          <EmployeeMentionField
            value={invitees}
            onChange={setInvitees}
            excludeLogins={[row?.employee_login || "", viewerLogin || ""].filter(Boolean)}
          />
        </FieldRow>

        {otherResources.length > 0 ? (
          <FieldRow
            label="Ressourcen"
            hint={
              anchorIsResource
                ? `„${anchorName}“ ist bereits Teil des Antrags. Weitere Ressourcen erhalten denselben Terminantrag.`
                : "Ausgewählte Ressourcen erhalten denselben Terminantrag. Das Büro gibt Mitarbeiter und Ressourcen gemeinsam frei."
            }
          >
            <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-separator px-3 py-2">
              {otherResources.map((resource) => {
                const label = (resource.label || "Ressource").trim();
                const checked = resourceIds.includes(resource.id);
                return (
                  <Checkbox
                    key={resource.id}
                    id={`mp-proposal-resource-${resource.id}`}
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setResourceIds((current) =>
                        next
                          ? current.includes(resource.id)
                            ? current
                            : [...current, resource.id]
                          : current.filter((id) => id !== resource.id),
                      );
                    }}
                    label={label}
                  />
                );
              })}
            </div>
          </FieldRow>
        ) : null}

        <FieldRow label="Notiz">
          <TextareaInput
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Optional"
          />
        </FieldRow>

        {conflicts.length > 0 ? (
          <Alert variant="warning">
            <p className="text-sm font-medium">Überschneidung mit Abwesenheit</p>
            <ul className="mt-1 list-inside list-disc text-xs">
              {conflicts.map((a) => (
                <li key={a.id}>
                  {formatDateDe(a.date_from)}
                  {a.date_to !== a.date_from ? ` – ${formatDateDe(a.date_to)}` : ""}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}
      </div>
    </Dialog>
  );
}
